import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, ArrowUp, ArrowDown, Image as ImageIcon, Upload, CalendarClock } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "coming_soon", label: "Coming Soon" },
  { value: "inactive", label: "Hidden" },
];

const KIND_OPTIONS = ["water", "coffee", "tea", "juice", "energy_performance", "functional_wellness", "custom"];

const emptyForm = {
  slug: "",
  name: "",
  kind: "custom",
  tagline: "",
  description: "",
  status: "coming_soon",
  badge_text: "COMING SOON",
  hero_image_url: "",
  display_order: 100,
  moq: "",
  launch_at: "",
};

const StatusPill = ({ status }) => {
  const color = status === "active"
    ? "bg-[var(--ft-gold)] text-[var(--ft-black)]"
    : status === "coming_soon"
    ? "bg-white text-[var(--ft-charcoal)] border border-[var(--ft-border)]"
    : "bg-[var(--ft-charcoal)] text-white/80";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.625rem] font-bold tracking-[0.18em] uppercase ${color}`}>
      {status.replace("_", " ")}
    </span>
  );
};

const AdminCollections = () => {
  const [collections, setCollections] = useState([]);
  const [moqStandard, setMoqStandard] = useState("");
  const [editing, setEditing] = useState(null);   // full collection object or null
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    api.get("/admin/collections").then((r) => {
      setCollections(r.data.collections || []);
      setMoqStandard(r.data.moq_standard || "");
    }).catch(() => toast.error("Failed to load collections"));
  };

  useEffect(() => { load(); }, []);

  const startEdit = (c) => {
    setEditing(c);
    setForm({
      slug: c.slug,
      name: c.name || "",
      kind: c.kind || "custom",
      tagline: c.tagline || "",
      description: c.description || "",
      status: c.status || "coming_soon",
      badge_text: c.badge_text || "",
      hero_image_url: c.hero_image_url || "",
      display_order: c.display_order || 100,
      moq: c.moq || "",
      launch_at: c.launch_at ? c.launch_at.substring(0, 16) : "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      delete payload.slug; // slug immutable on update
      // Convert datetime-local (YYYY-MM-DDTHH:mm) → ISO UTC or send empty string as null
      if (payload.launch_at) {
        payload.launch_at = new Date(payload.launch_at).toISOString();
      } else {
        payload.launch_at = null;
      }
      await api.patch(`/admin/collections/${editing.id}`, payload);
      toast.success("Collection updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const createNew = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.launch_at) {
        payload.launch_at = new Date(payload.launch_at).toISOString();
      } else {
        payload.launch_at = null;
      }
      await api.post("/admin/collections", payload);
      toast.success("Collection created");
      setShowNew(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  const uploadHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/uploads/image?purpose=collection-hero", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, hero_image_url: r.data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      e.target.value = ""; // reset input so same file can be re-picked
    }
  };

  const removeCollection = async (c) => {
    if (!window.confirm(`Delete collection "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/collections/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const reorder = async (c, delta) => {
    const newOrder = (c.display_order || 100) + delta;
    try {
      await api.patch(`/admin/collections/${c.id}`, { display_order: newOrder });
      load();
    } catch { toast.error("Reorder failed"); }
  };

  const quickStatus = async (c, status) => {
    try {
      await api.patch(`/admin/collections/${c.id}`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch { toast.error("Status update failed"); }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Collections</div>
          <h1 className="text-3xl font-bold tracking-tight text-[var(--ft-black)]">Product Collections Manager</h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Top-level beverage collections (Water, Coffee, Tea, Juice, etc.). Toggle status,
            edit hero imagery, and set the standardized MOQ shown across the public site.
          </p>
          <div className="mt-3 text-xs text-[var(--ft-muted)]">
            <span className="font-semibold text-[var(--ft-charcoal)]">Default:</span>{" "}
            <span className="font-mono">{moqStandard}</span>
          </div>
        </div>
        <button onClick={() => { setForm(emptyForm); setShowNew(true); }} className="ft-btn-primary" data-testid="new-collection-btn">
          <Plus size={16} /> New Collection
        </button>
      </div>

      <div className="ft-card p-0 overflow-hidden">
        <table className="ft-table" data-testid="admin-collections-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>Order</th>
              <th>Collection</th>
              <th>Kind</th>
              <th>Status</th>
              <th>MOQ</th>
              <th style={{ width: 220 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {collections.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[var(--ft-muted)] py-10">No collections yet.</td></tr>
            )}
            {collections.map((c) => (
              <tr key={c.id} data-testid={`collection-row-${c.slug}`}>
                <td>
                  <div className="flex items-center gap-1">
                    <button onClick={() => reorder(c, -1)} className="p-1 text-[var(--ft-muted)] hover:text-[var(--ft-gold)]" data-testid={`reorder-up-${c.slug}`}><ArrowUp size={14} /></button>
                    <span className="text-xs font-mono text-[var(--ft-muted)] w-6 text-center">{c.display_order}</span>
                    <button onClick={() => reorder(c, 1)} className="p-1 text-[var(--ft-muted)] hover:text-[var(--ft-gold)]" data-testid={`reorder-down-${c.slug}`}><ArrowDown size={14} /></button>
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-3">
                    {c.hero_image_url ? (
                      <img src={c.hero_image_url} alt="" className="h-10 w-14 object-cover rounded-md border border-[var(--ft-border)]" />
                    ) : (
                      <div className="h-10 w-14 rounded-md border border-dashed border-[var(--ft-border)] flex items-center justify-center text-[var(--ft-muted)]">
                        <ImageIcon size={14} />
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-[var(--ft-black)]">{c.name}</div>
                      <div className="text-xs text-[var(--ft-muted)] font-mono">{c.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="text-sm text-[var(--ft-charcoal)]">{c.kind}</td>
                <td><StatusPill status={c.status} /></td>
                <td className="text-xs text-[var(--ft-charcoal)] max-w-[280px] truncate" title={c.moq || moqStandard}>
                  {c.moq || <span className="italic text-[var(--ft-muted)]">default</span>}
                </td>
                <td>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => startEdit(c)} className="text-xs px-3 py-1.5 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]" data-testid={`edit-${c.slug}`}>
                      Edit
                    </button>
                    {c.status !== "active" && (
                      <button onClick={() => quickStatus(c, "active")} className="text-xs px-3 py-1.5 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]" data-testid={`activate-${c.slug}`}>Activate</button>
                    )}
                    {c.status !== "coming_soon" && (
                      <button onClick={() => quickStatus(c, "coming_soon")} className="text-xs px-3 py-1.5 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]" data-testid={`soon-${c.slug}`}>Coming Soon</button>
                    )}
                    {c.status !== "inactive" && (
                      <button onClick={() => quickStatus(c, "inactive")} className="text-xs px-3 py-1.5 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]" data-testid={`hide-${c.slug}`}>Hide</button>
                    )}
                    <button onClick={() => removeCollection(c)} className="p-1.5 rounded-full text-[var(--ft-muted)] hover:text-[var(--ft-error)]" data-testid={`delete-${c.slug}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(editing || showNew) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setEditing(null); setShowNew(false); }}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="ft-eyebrow mb-1">{editing ? "Edit" : "New"}</div>
                <h2 className="text-2xl font-bold tracking-tight">{editing ? editing.name : "New Collection"}</h2>
              </div>
              <button onClick={() => { setEditing(null); setShowNew(false); }} className="text-[var(--ft-muted)] hover:text-[var(--ft-black)]"><X size={22} /></button>
            </div>

            <form onSubmit={editing ? saveEdit : createNew} className="space-y-5" data-testid="collection-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ft-label">Name*</label>
                  <input required className="ft-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="col-name" />
                </div>
                <div>
                  <label className="ft-label">Slug*</label>
                  <input required disabled={!!editing} className="ft-input font-mono text-sm disabled:opacity-70" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="col-slug" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ft-label">Kind*</label>
                  <select className="ft-input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="col-kind">
                    {KIND_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div>
                  <label className="ft-label">Status*</label>
                  <select className="ft-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="col-status">
                    {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="ft-label">Tagline</label>
                <input className="ft-input" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="col-tagline" />
              </div>

              <div>
                <label className="ft-label">Description</label>
                <textarea rows={3} className="ft-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="col-description" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ft-label">Badge Text</label>
                  <input className="ft-input" placeholder="COMING SOON" value={form.badge_text} onChange={(e) => setForm({ ...form, badge_text: e.target.value })} data-testid="col-badge" />
                </div>
                <div>
                  <label className="ft-label">Display Order</label>
                  <input type="number" className="ft-input" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value, 10) || 0 })} data-testid="col-order" />
                </div>
              </div>

              <div>
                <label className="ft-label">Hero Image</label>
                <div className="flex gap-2 items-stretch">
                  <input className="ft-input flex-1" placeholder="/api/uploads/… or https://…" value={form.hero_image_url} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })} data-testid="col-hero" />
                  <label className="ft-btn-ghost cursor-pointer inline-flex items-center gap-1.5" data-testid="col-hero-upload-label">
                    <Upload size={14} /> Upload
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={uploadHero} data-testid="col-hero-upload" />
                  </label>
                </div>
                {form.hero_image_url && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-[var(--ft-border)] aspect-[16/9] bg-[var(--ft-light-gray)]">
                    <img src={form.hero_image_url} alt="preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              <div>
                <label className="ft-label inline-flex items-center gap-1.5">
                  <CalendarClock size={13} /> Scheduled Launch (optional)
                </label>
                <input type="datetime-local" className="ft-input" value={form.launch_at} onChange={(e) => setForm({ ...form, launch_at: e.target.value })} data-testid="col-launch-at" />
                <div className="text-xs text-[var(--ft-muted)] mt-1">
                  When this date arrives, a &quot;Coming Soon&quot; collection automatically flips to Active on the next public fetch.
                </div>
              </div>

              <div>
                <label className="ft-label">MOQ (optional override)</label>
                <input className="ft-input" placeholder={moqStandard} value={form.moq} onChange={(e) => setForm({ ...form, moq: e.target.value })} data-testid="col-moq" />
                <div className="text-xs text-[var(--ft-muted)] mt-1">
                  Leave blank to inherit the default: <span className="font-mono">{moqStandard}</span>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-[var(--ft-border)]">
                <button type="button" onClick={() => { setEditing(null); setShowNew(false); }} className="ft-btn-ghost">Cancel</button>
                <button type="submit" className="ft-btn-primary" data-testid="col-save">
                  {editing ? "Save Changes" : "Create Collection"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCollections;
